import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { useScopedAppFlagStore } from '@frontend/scope';
/**
 * This constant defines the set of features available for global search.
 * Each feature listed here is utilized for mapping types, generating unique identifiers, and facilitating search operations.
 * To integrate a new feature into the global search functionality:
 *  - Add the feature's name to this array.
 *  - Ensure the new feature supports type definition, unique identification, and search capabilities.
 */

const allFeatures = 'all';

export const globalSearchFeatures = [allFeatures, 'messaging', 'contacts', 'faxes', 'settings', 'features'] as const;

export type GlobalSearchFeatures = (typeof globalSearchFeatures)[number];

export const useGlobalSearchFeatureLabelMap = () => {
  const { t } = useTranslation('global-search');
  const tokens = useTranslationTokens();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasFaxSearch = getFeatureFlagValue('fax-global-search');
  const messagingEnabled = useMessagingFeaturesEnabled();

  return {
    all: t('All'),
    ...(messagingEnabled && { messaging: t('Messages') }),
    contacts: tokens.contacts,
    ...(hasFaxSearch && { faxes: t('Faxes') }),
    settings: t('Settings'),
    features: t('Features'),
  } satisfies Partial<Record<GlobalSearchFeatures, string>>;
};
